import type { WhaleState, Whales, World } from '../types';
import type { SiteId } from '../data/sites';
import { HUMPBACK, stepHumpback } from '../whales';
import { HUMPBACK_JANUARY, HUMPBACK_SUPPLY } from '../data/whales/humpback';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';

/** The humpbacks' draws on their population's stream of the day (`stockDraws`, one population): binomial deaths on each class's count, a Poisson draw of the day's calves. */
export function whaleDraws(world: { seed: number; day: number }, siteId: SiteId, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.humpback, siteId, streamOf);
}

/**
 * Step 3 of the tick, the animals, after the honu (M101): the humpbacks that
 * winter in each sanctuary's waters die and calve (`whales.ts`), on their own
 * stream of the day (a test hands in its own as `streamOf`).
 */
export function stepWhales(world: World, streamOf: typeof stockStream = stockStream): void {
  const epochDay = epochDayOf(world);
  for (const s of world.sanctuaries) {
    if (s.whales) stepHumpback(HUMPBACK, s.whales.humpback, epochDay, HUMPBACK_SUPPLY, whaleDraws(world, s.siteId, streamOf));
  }
}

/** Whether a site has humpbacks: Maui Nui, in the Hawaii population's winter grounds. */
export const siteHasWhales = (siteId: SiteId): boolean => siteId === 'maui_nui';

/**
 * The humpbacks as a world opens on a day: the balance's first of January
 * (`data/whales/`), stepped at expected values through the opening year's
 * days to the opening day, each class's count then rounded to a whole whale
 * (no draw: a world's opening draws nothing).
 */
export function openingWhales(epochDay: number): Whales {
  const year = civilFromEpochDay(epochDay).year;
  const humpback: WhaleState = { counts: [...HUMPBACK_JANUARY.counts] };
  for (let d = epochDayFromCivil(year, 1, 1); d <= epochDay; d++) stepHumpback(HUMPBACK, humpback, d, HUMPBACK_SUPPLY, EXPECTED_DRAWS);
  return { humpback: { counts: humpback.counts.map((n) => Math.round(n)) } };
}

/** A sanctuary's whales as a world opens on a day, where its site has them (`siteHasWhales`); elsewhere none. */
export function sanctuaryOpeningWhales(siteId: SiteId, epochDay: number): Whales | null {
  return siteHasWhales(siteId) ? openingWhales(epochDay) : null;
}
